import {useIsDarkMode} from '@pinecast/common/hooks/useIsDarkMode';
import * as React from 'react';
import type {JSX} from 'react';

// Every text and control pair these tokens draw is listed, with the ratio it
// owes, in `colors.contrast.test.ts`. Add a pair there when a component draws a
// new one.
//
// Pinecast, the Django app, copies a few of these into stylesheets that cannot
// import them: `background`, `contentText` and `headingBlue`, for the system's
// light and dark mode, in `templates/jinja2/base_css.html`. The page shows
// them until the scripts load, and always on Short Links and the Tip Jar.
// `colors.static.test.ts` fails when one changes, and says what to update.
export const designColors = {
  background: '#F8F4EB',

  contentBackground: '#fff',
  contentBorder: '#adacaa',
  contentText: '#090909',
  contentTextAccent: '#090909',
  // De-emphasized text (link :hover, secondary labels, placeholders, including
  // Stripe's card field). Text, so it owes 4.5:1 and cannot borrow
  // `borderGrayAccent`, which only owes the 3:1 non-text ratio.
  // Check translucent backdrops too: private rows paint rgba(0, 0, 0, 0.05), which
  // blends these surfaces down to #f2f2f2 / #ece8df.
  contentTextMuted: '#546a74',
  // Status text (importing / failed to import). `borderMagentaNormal` still suits
  // the Crown icon at 3:1, but as text it fails on every surface except #fff.
  contentTextMagenta: '#b80b68',
  // Error messages (InlineError) and their icon, on the page as well as on cards.
  contentTextError: '#b83232',

  // The edge of a control: text fields, and the track of a switch that is off.
  inputBorder: '#888',

  // A fill (primary buttons, checkboxes, the selected tab's underline), not a
  // text color. In dark mode white text on it needs it dark and text in it on
  // the dark surfaces needs it light, and no blue does both. Blue text is
  // `headingBlue`.
  accentBlue: '#090909',
  accentBlueLight: '#C7ECFA',
  accentBlueText: '#fff',
  borderBlueLight: '#E3F5FC',
  borderBlueNormal: '#C7ECFA',
  borderGrayAccent: '#5F7680',
  borderGrayLight: '#EDF4FF',
  borderGrayNormal: '#888',
  borderRedAccent: '#F05569',
  // The focus ring of a control that is already red: a field that is not
  // valid, and a destructive row of a menu (`focusRing`'s `danger` tone). It
  // owes 3:1 against the surfaces and against `badgeRedBackground`, the fill
  // of that row, so it is darker than its name says.
  borderRedLight: '#a3132d',
  headingBlue: '#090909',
  borderMagentaNormal: '#DF0D7E',
  // The track of a switch that is on, and the Scheduled icon. 3:1 against the
  // page, the card, a private row and the switch's white thumb.
  borderGreenNormal: '#12924a',
  // Check icons beside a status that is complete.
  borderGreenAccent: '#1f8f4a',
  // The status dot for a state that waits on something (`Indicator`'s
  // yellow). A yellow that clears 3:1 on the light surfaces is this dark.
  borderYellowAccent: '#9a6b00',

  // The focus indicator of every control (`focusRing` in `focusRing.ts`). It
  // is drawn outside the control with a gap, so it owes 3:1 against the
  // surfaces, not against the control's own fill: in light mode it is the
  // color of a primary button.
  focusRing: '#090909',

  badgeBlueText: '#335867',
  badgeBlueBackground: '#C7ECFA',
  badgeGrayText: '#44484d',
  badgeGrayBackground: '#e9edf1',
  badgeGreenText: '#004910',
  badgeGreenBackground: '#C4FF7E',
  badgeRedText: '#7E0B49',
  badgeRedBackground: '#FF92A1',
  badgeYellowText: '#73611a',
  badgeYellowBackground: '#F9F199',

  promotionText: '#090909',
  promotionBackground: '#F9F199',

  // The series of an analytics chart, in this order (`seriesColors` in
  // db-analytics). The server's colors were one set for both modes, and most
  // of them were under 3:1 on a light card. Each slot is a line, an area and
  // a legend chip, so it owes 3:1 against the card and the page. The hues and
  // their order keep neighbors apart for color blind readers; four steps here
  // are darker than in dark mode to clear 3:1 on the light surfaces.
  chartSeries1: '#2a78d6',
  chartSeries2: '#e5632e',
  chartSeries3: '#00a06e',
  chartSeries4: '#bd8004',
  chartSeries5: '#d36891',
  chartSeries6: '#008300',
  chartSeries7: '#4a3aa7',
  chartSeries8: '#e34948',
  // A series that is not one of the eight: "Other", and a rolling average.
  chartSeriesNeutral: '#8a8a8a',
};
export type ColorMap = typeof designColors;

// No spread of `designColors`: every token needs a value chosen for dark mode,
// and a spread let four of them ship with their light values.
export const designColorsDark: ColorMap = {
  background: '#090909',

  contentBackground: '#26262a',
  contentBorder: '#555',
  contentText: '#fff',
  contentTextAccent: '#ccc',
  contentTextMuted: '#949494',
  contentTextMagenta: '#f65caf',
  contentTextError: '#ff7575',

  inputBorder: '#888',

  accentBlue: '#0066FF',
  accentBlueLight: '#2f6196',
  accentBlueText: '#fff',

  borderBlueLight: '#28408C',
  borderBlueNormal: '#4B6884',
  borderGrayAccent: '#707070',
  borderGrayLight: '#555',
  borderGrayNormal: '#69778c',
  borderRedAccent: '#ff5c72',
  borderRedLight: '#ff8a9b',
  headingBlue: '#3c92ff',
  borderMagentaNormal: '#f0479f',
  borderGreenNormal: '#02a552',
  borderGreenAccent: '#3ccf6a',
  borderYellowAccent: '#f4ca1f',

  focusRing: '#fff',

  badgeBlueBackground: '#1a2d4a',
  badgeBlueText: '#78aefb',
  badgeGrayText: '#b8cce0',
  badgeGrayBackground: '#394b61',
  badgeGreenBackground: '#095a1b',
  badgeGreenText: '#79ff97',
  badgeRedBackground: '#6b261f',
  badgeRedText: '#ff809e',
  badgeYellowText: '#ffe266',
  badgeYellowBackground: '#675a03',

  promotionText: '#ffedba',
  promotionBackground: '#86660f',

  chartSeries1: '#3987e5',
  chartSeries2: '#d95926',
  chartSeries3: '#199e70',
  chartSeries4: '#c98500',
  chartSeries5: '#d55181',
  chartSeries6: '#068505',
  chartSeries7: '#9085e9',
  chartSeries8: '#e66767',
  chartSeriesNeutral: '#767676',
};

const ColorsContext = React.createContext(designColors);
export const ColorsProvider = ColorsContext.Provider;
export const useColors = () => React.useContext(ColorsContext);

export const DarkModeProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const darkMode = useIsDarkMode();
  return React.createElement(
    ColorsProvider,
    {value: darkMode ? designColorsDark : designColors},
    children,
  );
};

export const useSetBackground = () => {
  const colors = useColors();
  React.useLayoutEffect(() => {
    document.body.style.backgroundColor = colors.background;
    document.body.style.color = colors.contentText;
    document.body.parentElement!.style.backgroundColor = colors.background;
    document.body.parentElement!.style.color = colors.contentText;
  });
};
